import { getLogger } from '@storefront/logging';
import { UDL_PAGE_NAME } from '@storefront/services/udl/constants';
import { create } from 'zustand';

export type ProductType = {
  name: string;
  id: string;
  price?: number;
  brand?: string;
  category?: string;
  variant?: string;
  quantity?: number;
  discountAmount?: number;
};

export type PurchaseViewType = {
  currencyCode: string;
  purchase: PurchaseType;
};
type PurchaseType = {
  actionField: {
    id: string;
    revenue?: number;
    affiliation?: string;
    tax?: number;
  };
  products?: ProductType[];
};
export type CheckoutViewType = {
  currencyCode: string;
  checkout: {
    actionField: {
      step: number;
      option: string;
    };
    products: ProductType[];
  };
};

type AddToCartType = {
  currencyCode: string;
  add: {
    newCart: boolean; //  true if this is a new cart
    detail: {
      actionField: { list: string };
    };
    products: ProductType[];
  };
};

export type EventName =
  | 'e_pageView'
  | 'e_document'
  | 'e_linkClick'
  | 'e_formStart'
  | 'e_formSubmit'
  | 'e_addToCart';

export type LinkPlacementName =
  | 'pdp-config'
  | 'summary'
  | 'checkout'
  | 'order-confirmation';

export type UdlEvent = {
  event: EventName;
  linkID?: string;
  linkPlacement?: string;
  hasVisited?: string;
  ecommerce?: CheckoutViewType | PurchaseViewType | AddToCartType;
  products?: ProductType[];
  pageNameL5?: string;
  pageNameL8?: string;
};

type TrackPageView = {
  pageName: string;
  pathName: string;
  clientID: string;
};
export interface UdlStoreState {
  userTypeSession: string;
  appInstanceID: string;
  clientID: string;
  update: (data: Partial<UdlStoreState>) => void;
  trackPageView: (data: TrackPageView) => void;
  trackEvent: (data: Partial<UdlEvent>) => void;
}
const THREE_SECONDS_IN_MS = 3000;

const useUdlStore = create<UdlStoreState>((set, get) => ({
  userTypeSession: 'guest',
  appInstanceID: '',
  clientID: '',

  update: (data: Partial<UdlStoreState>) =>
    set((state) => ({ ...state, ...data })),
  trackPageView: ({ pageName, pathName, clientID }: TrackPageView) => {
    const logger = getLogger('udl-store');
    if (clientID) get().update({ clientID });

    const event: UdlEvent = {
      event: 'e_pageView',
      pageNameL5:
        pageName ||
        UDL_PAGE_NAME[pathName as keyof typeof UDL_PAGE_NAME] ||
        UDL_PAGE_NAME.UNDEFINED,

      ...get(),
    };

    try {
      window.dataLayer.push(event);
    } catch (error) {
      logger.error('[UDL] page view event not tracked!', error);
    }
  },
  trackEvent: (event: Partial<UdlEvent>) => {
    const logger = getLogger('udl-store');
    const fullEvent = { ...event, ...get() };
    if (fullEvent.appInstanceID === '') delete fullEvent.appInstanceID;

    const hasVisited =
      localStorage.getItem('hasVisited') !== null ? 'true' : 'false';
    fullEvent.hasVisited = hasVisited;

    const pushToDataLayer = (attempt = 0) => {
      if (window?.dataLayer) {
        window.dataLayer.push(fullEvent);
        logger.info('[UDL] event pushed successfully', fullEvent);
      } else if (attempt < 3) {
        setTimeout(() => pushToDataLayer(attempt + 1), THREE_SECONDS_IN_MS);
        logger.warn('[UDL] window.dataLayer not ready! Trying again', attempt);
      } else {
        logger.error(
          '[UDL] event not tracked! window.dataLayer not available.',
          { event: fullEvent }
        );
      }
    };

    try {
      pushToDataLayer();
    } catch (error) {
      logger.error('[UDL] event not tracked!', error);
    }
  },
}));

export default useUdlStore;
